import { useCallback, useEffect, useMemo, useState } from 'react';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { CircularProgress, RadioGroup, Typography } from '@mui/material';
import moment from 'moment';
import { useLocation, useParams } from 'react-router-dom';
import { PDF_EXPORT_INFO_MESSAGE } from 'common/constants/documents';
import { MultiplePagesExportOptions } from 'common/hooks/usePdfExport/types';
import { usePdfExport } from 'common/hooks/usePdfExport/usePdfExport';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { useIsCompanyPath } from 'utilities/useIsCompanyPath/useIsCompanyPath';
import { useIsFundPath } from 'utilities/useIsFundPath/useIsFundPath';
import { CompanySubOptions } from './components/CompanySubOptions';
import { FirmSubOptions } from './components/FirmSubOptions';
import { FundSubOptions } from './components/FundSubOptions';
import { RadioOption } from './components/RadioOption';
import { usePdfPages } from './hooks/usePdfPages';
import {
  ExportProgressContainer,
  MultiplePagesExportButton,  
  ProgressSubtext,
  ProgressText,
  StyledButton,
  StyledContentContainer,
  StyledDialog,
  StyledDialogActions,
  StyledDialogContent,
  StyledDialogTitle,
  StyledInfoIcon,
  StyledRadioContainer,
} from './PdfExportConfigDialog.styles';
import {
  DEFAULT_PDF_CONFIGURATION,
  PdfExportConfigDialogProps,
  PdfExportConfiguration,
  PdfExportOption,
} from './PdfExportConfigDialog.types';

const PdfExportConfigDialog = ({ open, onClose }: PdfExportConfigDialogProps) => {
  const [configuration, setConfiguration] = useState<PdfExportConfiguration>(DEFAULT_PDF_CONFIGURATION);

  // Determine current context
  const isCompanyPath = useIsCompanyPath();
  const isFundPath = useIsFundPath();  
  const isFirmPath = useMemo(() => !isCompanyPath && !isFundPath, [isCompanyPath, isFundPath]);

  let reportCoverTitle = 'PDF Report of';

  if (isCompanyPath) {
    reportCoverTitle = 'Valuation Report of';
  } else if (isFundPath) {
    reportCoverTitle = 'Fund Report of';
  } else if (isFirmPath) {
    reportCoverTitle = 'Firm Report of';
  }

  // Get entity information from store
  const [storeValues] = useStore() as unknown as UseStoreValues;
  const { companyInfo, firmInfo, fundInfo, fundList } = storeValues;
  const params = useParams();
  const location = useLocation();

  // Get the current entity name based on context
  const entityName = useMemo(() => {
    if (isCompanyPath && companyInfo?.name) {
      return companyInfo.name;
    }
    if (isFundPath) {
      // First check fundInfo from store
      if (fundInfo?.name) {
        return fundInfo.name;
      }
      // Otherwise find it from fundList using slug
      const fundSlug = params.fundSlugParam;
      if (fundSlug && fundList) {
        const currentFund = fundList.find(fund => fund.slug === fundSlug);
        return currentFund?.name;
      }      
    }
    if (isFirmPath && firmInfo?.name) {
      return firmInfo.name;
    }
    return '';
  }, [isCompanyPath, isFundPath, isFirmPath, companyInfo, firmInfo, fundInfo, fundList, params.fundSlugParam]);

  // Get measurement date from query string
  const measurementDate = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('date') || '';
  }, [location.search]);

  // Generate dynamic filename based on entity name and date
  const exportFilename = useMemo(() => {
    const parts = [];
    
    if (entityName) {
      // Sanitize entity name for filename (remove special characters)
      const sanitizedName = entityName.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      parts.push(sanitizedName);
    }
    
    if (measurementDate) {
      parts.push(measurementDate);
    }
    
    const filename = parts.length > 0 ? parts.join('-') : 'pdf-export';
    return filename;
  }, [entityName, measurementDate]);

  // Get dynamic URLs based on current context and configuration
  const pdfPages = usePdfPages(configuration);

  const isExportDisabled = pdfPages.length === 0;

  // Automatically set the selected option based on current context
  useEffect(() => {
    if (isCompanyPath) {
      setConfiguration(prev => ({ ...prev, selectedOption: PdfExportOption.VALUATIONS }));
    } else if (isFundPath) {
      setConfiguration(prev => ({ ...prev, selectedOption: PdfExportOption.FUND }));
    } else if (isFirmPath) {
      setConfiguration(prev => ({ ...prev, selectedOption: PdfExportOption.FIRM }));
    }
  }, [isCompanyPath, isFundPath, isFirmPath]);

  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({
    currentPage: 0,
    totalPages: 0,
    percentage: 0,
    isProcessing: false,
  });

  const { exportMultiplePagesAsPdf } = usePdfExport();

  const handleOptionChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedOption = event.target.value as PdfExportOption;
    setConfiguration(prev => {
      const newConfig = {
        ...prev,
        selectedOption,
      };

      // When switching to VALUATIONS, set all company options to true
      if (selectedOption === PdfExportOption.VALUATIONS) {
        newConfig.companyOptions = {
          valuationSummary: true,
          equityAllocation: true,
          concludedShareValues: true,
          financials: true,
          fundOwnership: true,
          capTable: true,
          breakpointAnalysis: true,
          backsolve: true,
        };
      } else if (selectedOption === PdfExportOption.OTHER) {
        // When switching to OTHER, set all company options to false
        newConfig.companyOptions = {
          valuationSummary: false,
          equityAllocation: false,
          concludedShareValues: false,
          financials: false,
          fundOwnership: false,
          capTable: false,
          breakpointAnalysis: false,
          backsolve: false,
        };
      }

      return newConfig;
    });
  }, []);

  const handleCompanyOptionsChange = useCallback((options: typeof configuration.companyOptions) => {
    setConfiguration(prev => ({
      ...prev,
      companyOptions: options,
    }));
  }, [configuration]);

  const handleFundOptionsChange = useCallback((options: typeof configuration.fundOptions) => {
    setConfiguration(prev => ({
      ...prev,
      fundOptions: options,
    }));
  }, [configuration]);

  const handleFirmOptionsChange = useCallback((options: typeof configuration.firmOptions) => {
    setConfiguration(prev => ({
      ...prev,
      firmOptions: options,
    }));
  }, [configuration]);

  const handleMultiplePagesExport = useCallback(async () => {
    try {
      setIsExporting(true);
      setExportProgress({ currentPage: 0, totalPages: pdfPages.length, percentage: 0, isProcessing: false });            

      const multiplePagesOptions: MultiplePagesExportOptions = {
        entityName,
        coverTitle: reportCoverTitle,
        dateOfIssue: `Issued: ${moment().format('MMMM D, YYYY')}`,
        pages: pdfPages,
        filename: exportFilename,        
        onProgress: (currentPage, totalPages, percentage) => {          
          const calculatedPercentage = Math.round((currentPage / totalPages) * 100);
          const finalPercentage = percentage !== undefined ? Math.round(percentage) : calculatedPercentage;
          setExportProgress({ currentPage, totalPages, percentage: finalPercentage, isProcessing: true });
        },
      };

      await exportMultiplePagesAsPdf(multiplePagesOptions);

      setIsExporting(false);
      onClose();
    } catch {
      setIsExporting(false);
      setExportProgress({ currentPage: 0, totalPages: 0, percentage: 0, isProcessing: false });
      onClose();
    }
  }, [exportMultiplePagesAsPdf, onClose, pdfPages, exportFilename, entityName, reportCoverTitle]);

  const handleDialogClose = useCallback(() => {
    if (isExporting) {
      return;
    }
    onClose();
  }, [isExporting, onClose]);

  return (
    <StyledDialog 
      open={open} 
      onClose={handleDialogClose} 
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown={isExporting}
    >
      <StyledDialogTitle>{`${entityName} PDF Export`}</StyledDialogTitle>

      <StyledDialogContent>
        {isExporting && (
          <ExportProgressContainer>
            <CircularProgress size={60} />
            <ProgressText>{exportProgress.isProcessing ? 'Processing...' : 'Preparing export...'}</ProgressText>
            <ProgressSubtext>
              {exportProgress.isProcessing
                ? `Page ${exportProgress.currentPage} of ${exportProgress.totalPages} (${exportProgress.percentage}%)`
                : 'Setting up your PDF export...'}
            </ProgressSubtext>
          </ExportProgressContainer>
        )}

        <StyledRadioContainer>
          <RadioGroup value={configuration.selectedOption} onChange={handleOptionChange}>
            {isCompanyPath && (
              <>
                <RadioOption value={PdfExportOption.VALUATIONS} label="Valuations" />
                <RadioOption value={PdfExportOption.OTHER} label="Other" />
                <CompanySubOptions
                  options={configuration.companyOptions}
                  onOptionsChange={handleCompanyOptionsChange}
                  showCondition={configuration.selectedOption === PdfExportOption.OTHER}
                />
              </>
            )}

            {isFundPath && (
              <>
                <RadioOption value={PdfExportOption.FUND} label="Fund" />
                <FundSubOptions
                  options={configuration.fundOptions}
                  onOptionsChange={handleFundOptionsChange}
                  showCondition={configuration.selectedOption === PdfExportOption.FUND}
                />
              </>
            )}

            {isFirmPath && (
              <>
                <RadioOption value={PdfExportOption.FIRM} label="Firm" />
                <FirmSubOptions
                  options={configuration.firmOptions}
                  onOptionsChange={handleFirmOptionsChange}
                  showCondition={configuration.selectedOption === PdfExportOption.FIRM}
                />
              </>
            )}
          </RadioGroup>
        </StyledRadioContainer>

        <StyledContentContainer>
          <StyledInfoIcon>
            <InfoIcon color="action" />
          </StyledInfoIcon>
          <Typography>{PDF_EXPORT_INFO_MESSAGE}</Typography>
        </StyledContentContainer>
      </StyledDialogContent>

      <StyledDialogActions>
        <StyledButton onClick={onClose}>Cancel</StyledButton>
        <MultiplePagesExportButton
          onClick={handleMultiplePagesExport}
          disabled={isExportDisabled || isExporting}
          isExporting={isExporting}>
          {isExporting ? 'Exporting...' : 'Export PDF'}
        </MultiplePagesExportButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default PdfExportConfigDialog;
